/**
 * Gallery of the screenshots a turn pushed (`forge_screenshot` frames). The
 * newest is shown large; the rest as thumbnails. Everything is in-memory for
 * the page session — the runtime keeps the files in its workspace.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ForgeScreenshot } from '../types';

interface ForgeScreenshotsProps {
  screenshots: ForgeScreenshot[];
  onClear?: () => void;
}

export function ForgeScreenshots({ screenshots, onClear }: ForgeScreenshotsProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const current =
    screenshots.find((s) => s.receivedAt === selected) ?? screenshots[screenshots.length - 1];

  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="forge-screenshots">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Screenshots</h3>
        {screenshots.length > 0 && onClear && (
          <Button size="sm" variant="ghost" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      {screenshots.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Claude Code sends a screenshot whenever it looks at the running app.
        </p>
      ) : (
        <>
          {current && (
            <figure className="space-y-1">
              <img
                src={current.dataUrl}
                alt={`Screenshot ${current.path}`}
                className="w-full rounded border bg-background"
              />
              <figcaption className="text-xs text-muted-foreground truncate" title={current.path}>
                {current.path}
              </figcaption>
            </figure>
          )}
          {screenshots.length > 1 && (
            <div className="flex gap-1 overflow-x-auto">
              {screenshots.map((s) => (
                <button
                  key={s.receivedAt}
                  type="button"
                  onClick={() => setSelected(s.receivedAt)}
                  className={cn(
                    'h-12 w-16 flex-shrink-0 overflow-hidden rounded border',
                    current?.receivedAt === s.receivedAt ? 'ring-2 ring-primary' : 'opacity-70',
                  )}
                  title={s.path}
                >
                  <img src={s.dataUrl} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
